"use client";

import { SizesUI } from "./SizesUI";
import { StandardProps } from "../componentMap";
import { hasSizes } from "../../utils/product/hasSizes";
import { FitToolAndSizeChart } from "../FitToolAndSizeChart/FitToolAndSizeChart";
import { useAvailableSizes } from "./utils";
import {
  buttonSizeThresholdConfigSchema,
  ButtonSizeThreshold,
} from "./config/buttonSizeThresholdConfigSchema";
import { HTMLAttributes, useEffect, useMemo } from "react";
import { ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED } from "../../constants/constants";
import { getBasket } from "@/utils/analytics/getBasket";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import { Variant } from "@/types/types";
import { useVariant } from "@/hooks/useVariant/useVariant";

export function Sizes({
  product,
  configProps,
  runtimeConfig,
  flags,
  experiencesRawObject,
}: Pick<
  StandardProps,
  "product" | "configProps" | "runtimeConfig" | "flags" | "experiencesRawObject"
>) {
  const isGroupingAllowed = configProps?.allowGrouping === true;
  const isSortingEnabled = configProps?.sortSizes === true;
  const withFitToolAndSizeChart = configProps?.withFitToolAndSizeChart === true;
  const showMore =
    typeof configProps?.showMore === "number"
      ? configProps?.showMore
      : undefined;
  const buttonSizeStyleThreshold: ButtonSizeThreshold | undefined =
    buttonSizeThresholdConfigSchema.safeParse(
      configProps?.buttonSizeStyleThreshold,
    )?.data || undefined;
  const { brand } = product;
  const { variant } = useVariant(product);

  const showPromoOnIndicators = !!flags.showPromoOnIndicators.value;
  const replaceHistoryEntryOnVariantChange =
    flags.replaceHistoryEntryOnVariantChange.value === true;
  const showPriceMatchedNonPriceEstablishedLabel =
    flags.showPriceMatchedNonPriceEstablishedLabel.value === true;

  const sizesList = useAvailableSizes(
    product,
    {
      sortSizes: isSortingEnabled,
    },
    { showPromoOnIndicators, showPriceMatchedNonPriceEstablishedLabel },
  );

  useEffect(() => {
    const sizeAddedToBasket = () => {
      const { product: basketProducts } = getBasket();
      const sizesForProduct = basketProducts.filter(
        (item) => item.productId === product.aliases.pimProductId,
      );

      const colourProducts = product.variants.filter(
        (v: Variant) =>
          v?.differentiators?.colour === variant.differentiators?.colour &&
          sizesForProduct.some((item) => item.sku === v.aliases.skuId),
      );

      if (colourProducts.length < 2) {
        return;
      }

      if (colourProducts.length > 2) {
        dispatchMonetateQEvent("MultipleSizesOfSameColourA2B");
      } else if (colourProducts.length > 1) {
        dispatchMonetateQEvent("2SizesOfSameColourA2B");
      }
    };
    window.addEventListener(
      ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
      sizeAddedToBasket,
    );
    return () => {
      window.removeEventListener(
        ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
        sizeAddedToBasket,
      );
    };
  }, []);

  const FitToolAndSizeChartComponent = useMemo(() => {
    return function FitToolAndSizeChartComponent(
      props: HTMLAttributes<HTMLDivElement>,
    ) {
      return (
        <div {...props}>
          <FitToolAndSizeChart
            product={product}
            runtimeConfig={runtimeConfig}
            experiencesRawObject={experiencesRawObject}
          />
        </div>
      );
    };
  }, [product, runtimeConfig, experiencesRawObject]);

  if (!hasSizes(product)) {
    return null;
  }

  return (
    <SizesUI
      sizes={sizesList}
      isGroupingAllowed={isGroupingAllowed}
      showMore={showMore}
      buttonSizeStyleThreshold={buttonSizeStyleThreshold}
      brand={brand?.name}
      replaceHistoryEntryOnVariantChange={replaceHistoryEntryOnVariantChange}
      FitToolAndSizeChartComponent={FitToolAndSizeChartComponent}
      withFitToolAndSizeChart={withFitToolAndSizeChart}
    />
  );
}
